import { mockTelegramEnv, serializeInitDataQuery } from '@telegram-apps/sdk-react';

/**
 * Mock Telegram environment для local dev (вне Telegram-клиента).
 *
 * Активируется в `main.tsx` если `env.VITE_USE_TG_MOCK === true`. Должен быть
 * вызван ДО `initSDK()` — иначе SDK не найдёт launch params в storage.
 *
 * Источники API:
 * - `mockTelegramEnv` — node_modules/@telegram-apps/bridge/dist/dts/env/mockTelegramEnv.d.ts,
 *   реэкспортируется через @telegram-apps/sdk → @telegram-apps/sdk-react.
 * - `serializeInitDataQuery` — node_modules/@telegram-apps/transformers/dist/dts/serializers/.
 *
 * ВНИМАНИЕ (CANON SP1): mock initData содержит фейковый hash/signature и НЕ пройдёт
 * валидацию HMAC на сервере. Для local dev mock используется только в комбинации
 * с моком API или с dev-режимом сервера, где HMAC отключён. Production-сборки
 * НЕ должны включать `VITE_USE_TG_MOCK=true`.
 *
 * Веха 13.1: платформа и тема параметризуются (`?tgPlatform=`/`?tgTheme=` в
 * main.tsx), чтобы вживую проверять `<AppRoot platform/appearance>` и нативные
 * формы контролов под iOS/Android/Desktop без сборки под Telegram.
 *
 * ⚠️ Хардкод hex ниже — это ТЕСТОВЫЕ ДАННЫЕ ТЕМЫ (эмуляция того, что Telegram
 * прислал бы в `tgWebAppThemeParams`), а НЕ цвета нашего UI. Выведены из-под
 * чек-листа F2 «нет хардкод-цветов» (Веха 13.1).
 */

export type MockPlatform = 'ios' | 'android' | 'tdesktop' | 'macos';
export type MockTheme = 'light' | 'dark';

/** Тестовая светлая тема Telegram (приближение к нативной Day-палитре). */
const LIGHT_THEME_PARAMS = {
  bg_color: '#ffffff',
  text_color: '#000000',
  hint_color: '#999999',
  link_color: '#3390ec',
  button_color: '#3390ec',
  button_text_color: '#ffffff',
  secondary_bg_color: '#f0f0f0',
  section_bg_color: '#ffffff',
  section_separator_color: '#e7e7e7',
  subtitle_text_color: '#82868a',
  accent_text_color: '#3390ec',
  destructive_text_color: '#e53935',
  section_header_text_color: '#3390ec',
} as const;

/** Тестовая тёмная тема Telegram (приближение к нативной Night-палитре). */
const DARK_THEME_PARAMS = {
  bg_color: '#17212b',
  text_color: '#f5f5f5',
  hint_color: '#708499',
  link_color: '#6ab3f3',
  button_color: '#5288c1',
  button_text_color: '#ffffff',
  secondary_bg_color: '#232e3c',
  section_bg_color: '#17212b',
  section_separator_color: '#101921',
  subtitle_text_color: '#708499',
  accent_text_color: '#6ab3f3',
  destructive_text_color: '#ec3942',
  section_header_text_color: '#6ab3f3',
} as const;

export function applyMockTelegramEnv(opts?: {
  platform?: MockPlatform;
  theme?: MockTheme;
  premium?: boolean;
  /**
   * Dev-affordance (work-020): кладёт `tgWebAppStartParam` в mock launchParams,
   * чтобы вживую открыть ASK-экран (`?tgStartParam=box_mocktoken1234`). Парсится
   * в main.tsx из `?tgStartParam=`. Вне Telegram start_param взять негде.
   */
  startParam?: string;
}): void {
  // Дефолт темы — dark: текущая палитра приложения тёмная, не «мигаем» в light.
  const platform = opts?.platform ?? 'tdesktop';
  const theme = opts?.theme ?? 'dark';

  const initDataRaw = serializeInitDataQuery({
    auth_date: new Date(),
    hash: 'mock-hash-not-valid-for-server',
    signature: 'mock-signature-not-valid-for-server',
    user: {
      id: 1,
      first_name: 'Mock',
      last_name: 'User',
      username: 'mock_user',
      language_code: 'ru',
      is_bot: false,
      is_premium: opts?.premium ?? false,
    },
  });

  mockTelegramEnv({
    launchParams: {
      tgWebAppData: initDataRaw,
      tgWebAppPlatform: platform,
      tgWebAppVersion: '8.0',
      tgWebAppThemeParams:
        theme === 'dark' ? DARK_THEME_PARAMS : LIGHT_THEME_PARAMS,
      ...(opts?.startParam
        ? { tgWebAppStartParam: opts.startParam }
        : {}),
    },
  });
}
